import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import apiManager from "../../api/apiManager";
import API_ENDPOINTS from "../../api/apiEndpoints";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../shared/Services/toasterService";
import {
  showLoader,
  hideLoader,
} from "../../shared/Services/Loader/loaderService";
import { useLocalization } from "../../context/LocalizationContext";
import { formatPhoneNumber } from "../../shared/helpers/formUtils";
import { getCurrencySymbol } from "../../utils/currencySymbols";
import { normalizeCurrencyPrice } from "../../utils/price";
import PrintableRepairOrder from "./common/PrintableRepairOrder";
import Swal from "sweetalert2";

const PrintPDFPage = () => {
  const location = useLocation();
  const { tokenId } = useParams();
  const [orderData, setOrderData] = useState(null);
  const [isApproved, setIsApproved] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [tokenError, setTokenError] = useState(false);
  const state = location.state || {};
  const repairOrderId = state?.repairOrderId;
  const { t: localization } = useLocalization();
  const currentYear = new Date().getFullYear();
  const dealerLogoSrc = "/assets/images/logo/kia-logo.png";
  const [isDeclined, setIsDeclined] = useState(false);

  useEffect(() => {
    let timer;
    if (tokenId) {
      showLoader();
      const payload = { token: tokenId };
      apiManager
        .post(API_ENDPOINTS.REPAIR_ORDER_TOKEN, payload)
        .then((res) => {
          const parsedData = res.data;
          setOrderData(parsedData);
          setTokenError(false);
          timer = setTimeout(
            () => {
              setTokenError(true);
              setOrderData(null);
            },
            2 * 60 * 1000,
          );
        })
        .catch(() => {
          setTokenError(true);
        })
        .finally(hideLoader());
    } else if (repairOrderId) {
      const payload = { repairOrderId: state.repairOrderId };
      apiManager
        .post(API_ENDPOINTS.REPAIR_ORDER_PRINT, payload)
        .then((res) => {
          const parsedData = res.data;
          setOrderData(parsedData);
        })
        .catch((err) => {
          showErrorMessage(err);
        })
        .finally(hideLoader());
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [tokenId, repairOrderId]);

  const getCategoryLabel = (category) => {
    const map = {
      oem: "O",
      recommended: "R",
      optional: "O",
    };
    return map[category?.toLowerCase()] || "O";
  };

  const changeStatusToSigned = async () => {
    if (!orderData?.repairOrder?.id) {
      showErrorMessage(localization("error_repair_order_id_not_available"));
      return;
    }
    try {
      showLoader();
      const payload = {
        repairOrderId: orderData.repairOrder.id,
      };

      const response = await apiManager.post("/dealer/order/status", payload);

      if (response.status === 200) {
        showSuccessMessage(localization("order_update_success"));
      } else {
        showErrorMessage(localization("error_create_self_signed_user_failed"));
      }
    } catch (error) {
      showErrorMessage(error);
    } finally {
      hideLoader();
    }
  };

  const declineRepairOrder = async () => {
    if (!orderData?.repairOrder?.id) {
      showErrorMessage(localization("error_repair_order_id_not_available"));
      return;
    }
    try {
      showLoader();
      const payload = {
        repairOrderId: orderData.repairOrder.id,
      };

      const response = await apiManager.post(
        "/dealer/order/link/expire",
        payload,
      );

      if (response.status === 200) {
        showSuccessMessage(localization("order_declined_success"));
      } else {
        showErrorMessage(localization("error_decline_order_failed"));
      }
    } catch (error) {
      showErrorMessage(error);
    } finally {
      hideLoader();
    }
  };

  const getTagClass = (category) => {
    const map = {
      oem: "oem-tag",
      recommended: "recommended-tag",
      optional: "optional-tag",
    };
    return map[category?.toLowerCase()] || "optional-tag";
  };

  const getCategoryClass = (category) => {
    const map = {
      oem: "teal",
      recommended: "blue",
      optional: "green",
    };
    return map[category?.toLowerCase()] || "gray";
  };

  const activeRepairOrderItems =
    orderData?.repairOrderItem?.filter((item) => item.status !== 3) || [];

  const getRepairOrderItemKey = (item, index) =>
    item.id || item.serviceId || `${item.serviceName || "service"}-${index}`;

  const updateRepairOrderItems = async (items) => {
    const repairOrder = orderData?.repairOrder;
    const dealershipId =
      repairOrder?.dealershipId ||
      repairOrder?.dealerShipId ||
      orderData?.dealerShipData?.id;
    const customerId = repairOrder?.customerId || orderData?.customerData?.id;
    const customerVehicleId =
      repairOrder?.customerVehicleId ||
      repairOrder?.customerVehicle?.id ||
      orderData?.customerVehicleData?.id;

    if (
      !repairOrder?.id ||
      !dealershipId ||
      !customerId ||
      !customerVehicleId
    ) {
      showErrorMessage(localization("error_repair_order_id_not_available"));
      return false;
    }

    const payload = {
      repairOrderId: repairOrder.id,
      dealershipId,
      customerId,
      customerVehicleId,
      milesOk: repairOrder?.milesOk ?? true,
      dateOfService: repairOrder?.dateOfService || new Date(),
      statusCode: repairOrder?.statusCode || 2,
      action: repairOrder?.action,
      items: items.map((item) => ({
        serviceCategory: item.serviceCategory || item.type,
        serviceName: item.serviceName || item.displayName,
        serviceId: item.serviceId || item.id,
        serviceDescription: item.serviceDescription || item.description,
        serviceBucket: item.serviceBucket,
        laborHours: item.laborHours,
        price: normalizeCurrencyPrice(item.price),
        isMileageBased: item.isMileageBased || false,
        minMileage: item.minMileage,
        maxMileage: item.maxMileage,
        source: item.source,
        status: item.status ?? 1,
      })),
    };

    await apiManager.post(API_ENDPOINTS.REPAIR_ORDER_UPDATE, payload);
    return true;
  };

  const handleDeclinePopup = (serviceKey = null, isAllDecline = false) => {
    Swal.fire({
      title: localization("confirm_decline"),
      text: isAllDecline ? localization("confirm_decline_service_order") : localization("confirm_decline_service"),
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: localization("cancel"),
      confirmButtonText: localization("ok"),
      confirmButtonColor: "#227246",
      cancelButtonColor: "#6c757d",
      width: "280px",
      padding: "0.5em",
      iconColor: "#f39c12",
      customClass: {
        popup: "swal2-small-popup",
        title: "swal2-small-title",
        icon: "swal2-small-icon",
        confirmButton: "btn btn-danger btn-sm",
        cancelButton: "btn btn-secondary btn-sm",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        if (isAllDecline) {
          setIsApproving(true);
          setIsDeclined(true);
          try {
            await declineRepairOrder();
          } finally {
            setIsApproving(false);
          }
        } else {
          handleDeclineService(serviceKey);
        }
      }
    });
  };

  const handleDeclineService = (serviceKey) => {
    setOrderData((prev) => {
      if (!prev?.repairOrderItem) return prev;

      return {
        ...prev,
        repairOrderItem: prev.repairOrderItem.map((item, index) =>
          getRepairOrderItemKey(item, index) === serviceKey
            ? { ...item, status: 3 }
            : item,
        ),
      };
    });
  };

  const getTotalByCategory = (category) => {
    const services = activeRepairOrderItems;
    return (
      services
        ?.filter((item) => item.serviceCategory?.toLowerCase() === category)
        ?.reduce((sum, item) => sum + (item.price || 0), 0)
        ?.toFixed(2) || "0.00"
    );
  };

  const subtotalAmount =
    activeRepairOrderItems?.reduce(
      (total, item) => total + (parseFloat(item.price) || 0),
      0,
    ) || 0;
  const taxRate = Number(orderData?.dealerShipData?.tax) || 0;
  const taxAmount = subtotalAmount * (taxRate / 100);
  const totalWithTax = subtotalAmount + taxAmount;
  const currencyCode = orderData?.dealerShipData?.currencyCode || "USD";

  if (tokenError) {
    return (
      <div
        className="d-flex justify-content-center align-items-center link-expired"
        style={{ minHeight: "100vh" }}
      >
        <div className="text-center">
          <div className="card">
            <div className="icon-wrapper">
              <div className="icon">
                <img
                  src="../assets/images/illustrations/exclamation-icon.svg"
                  alt="exclamation Icon"
                />
              </div>
            </div>
            <div className="card-body">
              <h3 className="mb-3">Oh no! The Link is Broken.</h3>

              <div className="alert-box">
                <p className="text-start">
                  <svg
                    className="w-5 h-5 mr-2"
                    width="24"
                    height="24"
                    class="me-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                  What to do next:
                </p>
                <ul className="text-start">
                  <li>Sorry, this link has expired or is no longer valid.</li>
                  <li>
                    Please get in touch with your{" "}
                    <strong>Shop administrator.</strong>
                  </li>
                  <li>Thank you for your understanding.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleApprovePopup = () => {
    Swal.fire({
      title: localization("confirm_approve"),
      text: localization("confirm_approve_service"),
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: localization("cancel"),
      confirmButtonText: localization("ok"),
      confirmButtonColor: "#227246",
      cancelButtonColor: "#6c757d",
      width: "280px",
      padding: "0.5em",
      iconColor: "#f39c12",
      customClass: {
        popup: "swal2-small-popup",
        title: "swal2-small-title",
        icon: "swal2-small-icon",
        confirmButton: "btn btn-danger btn-sm",
        cancelButton: "btn btn-secondary btn-sm",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsApproving(true);
        try {
          if (activeRepairOrderItems.length === 0) {
            showErrorMessage(
              localization("error_missing_required_or_no_services"),
            );
            return;
          }

          const didUpdate = await updateRepairOrderItems(
            orderData?.repairOrderItem || [],
          );

          if (!didUpdate) {
            return;
          }

          await changeStatusToSigned();
          setIsApproved(true);
        } finally {
          setIsApproving(false);
        }
      }
    });
  };

  return (
    <div className={`print-pdf-page content-wrapper ${tokenId ? "pt-2" : ""}`}>
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="select-services-content">
              <div className="container-fluid p-0">
                <div className="row">
                  <div className="col-md-12">
                    <div className="card mb-4 p-3">
                      <div className="container-fluid p-0">
                        <div className="row">
                          <div className="col-md-6 d-flex align-items-center gap-2">
                            <div className="address">
                              <h3>{orderData?.dealerShipData.name}</h3>
                              <div className="d-flex address-text">
                                <p className="mb-0">
                                  {[
                                    orderData?.dealerShipData.address1,
                                    orderData?.dealerShipData.address2,
                                    orderData?.dealerShipData.address3,
                                    orderData?.dealerShipData.cityOrLocality,
                                    orderData?.dealerShipData.postalCodeOrZip,
                                  ]
                                    .filter(Boolean)
                                    .join(", ")}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6 text-end d-flex  justify-content-end align-items-center gap-2">
                            <button
                              className="btn btn-secondary"
                              onClick={() => {
                                const originalTitle = document.title;
                                const id =
                                  orderData?.repairOrder?.id || "Service";
                                const today = new Date()
                                  .toISOString()
                                  .slice(0, 10);
                                document.title = `Service_${id}_${today}`;
                                window.print();
                                setTimeout(() => {
                                  document.title = originalTitle;
                                }, 1000);
                              }}
                              disabled={!repairOrderId && !isApproved}
                            >
                              {localization("print")}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <h1>
                      {localization("service")} :{orderData?.repairOrder?.id}
                    </h1>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="card mb-4">
                      <div className="card-header">
                        <h3 className="card-title">
                          {localization("vehicle")}
                        </h3>
                      </div>
                      <div className="card-body">
                        <ul>
                          <li className="mb-1">
                            <strong>
                              {orderData?.customerVehicleData?.year ||
                                localization("year")}{" "}
                              {orderData?.customerVehicleData?.makeName ||
                                localization("make")}{" "}
                              {orderData?.customerVehicleData?.modelName ||
                                localization("model")}
                            </strong>
                          </li>
                          <li className="mb-1">
                            {localization("vin")}:{" "}
                            {orderData?.customerVehicleData?.vin ||
                              localization("not_available")}
                          </li>
                          <li>
                            {localization("vehicle_id")}:{" "}
                            {orderData?.customerVehicleData?.modelId ||
                              localization("default_vehicle_id")}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card mb-4">
                      <div className="card-header">
                        <h3 className="card-title">
                          {localization("customer")}
                        </h3>
                      </div>
                      <div className="card-body">
                        <ul className="list-unstyled">
                          <li className="d-flex gap-3 mb-1">
                            <img
                              src="/assets/images/icons/outlined-user.svg"
                              alt="img"
                            />
                            <strong>
                              {orderData?.customerData?.fullName ||
                                localization("not_available")}
                            </strong>
                          </li>
                          <li className="d-flex gap-3 mb-1">
                            <img
                              src="/assets/images/icons/outlined-call.svg"
                              alt="img"
                            />
                            {orderData?.customerData?.phoneNumber
                              ? formatPhoneNumber(
                                  orderData.customerData.phoneNumber,
                                )
                              : localization("not_available")}
                          </li>
                          <li className="d-flex gap-3">
                            <img
                              src="/assets/images/icons/outlined-mail.svg"
                              alt="img"
                            />
                            {orderData?.customerData?.email ||
                              localization("not_available")}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12">
                    <div className="card mb-4">
                      <div className="card-header bg-gray p-4">
                        <ul className="price-section list-unstyled m-0 d-flex gap-2">
                          <li>
                            <div className="price-item teal-item">
                              <div className="price-tag">O</div>
                              <div className="price fw-bold">
                                {getCurrencySymbol(currencyCode)}
                                {getTotalByCategory("oem")}
                              </div>
                            </div>
                          </li>
                          <li>
                            <div className="price-item blue-item">
                              <div className="price-tag">R</div>
                              <div className="price fw-bold">
                                {getCurrencySymbol(currencyCode)}
                                {getTotalByCategory("recommended")}
                              </div>
                            </div>
                          </li>
                          <li>
                            <div className="price-item green-item">
                              <div className="price-tag">O</div>
                              <div className="price fw-bold">
                                {getCurrencySymbol(currencyCode)}
                                {getTotalByCategory("optional")}
                              </div>
                            </div>
                          </li>
                        </ul>
                      </div>
                      <div className="card-body">
                        <div className="container">
                          <div className="row">
                            <div className="col-lg-12 selected-card">
                              <div className="table-responsive">
                                <table className="table">
                                  <tbody>
                                    {orderData?.repairOrderItem?.map(
                                      (item, index) => {
                                        const serviceKey =
                                          getRepairOrderItemKey(item, index);
                                        const isItemDeclined =
                                          item.status === 3;

                                        return (
                                          <tr
                                            key={serviceKey}
                                            style={
                                              isItemDeclined
                                                ? { opacity: 0.55 }
                                                : undefined
                                            }
                                          >
                                            <td
                                              className={`border-0 tag-border ${getCategoryClass(
                                                item.serviceCategory,
                                              )}-text`}
                                            >
                                              <span
                                                className={`tag ${getTagClass(
                                                  item.serviceCategory,
                                                )}`}
                                              >
                                                {getCategoryLabel(
                                                  item.serviceCategory,
                                                )}
                                              </span>
                                            </td>
                                            <td className="border-0">
                                              <h3>
                                                {item.serviceName ||
                                                  localization(
                                                    "unnamed_service",
                                                  )}
                                              </h3>
                                              {/* <p className="mb-0">
                                              {item.serviceDescription ||
                                                item.source ||
                                                localization(
                                                  "no_description_provided",
                                                )}
                                            </p> */}
                                              {item.status === 3 && (
                                                <div
                                                  style={{
                                                    display: "inline-block",
                                                    backgroundColor: "#e0e0e0",
                                                    color: "#666666",
                                                    fontSize: "0.7em",
                                                    marginTop: "4px",
                                                    fontWeight: "400",
                                                    padding: "2px 8px",
                                                    borderRadius: "3px",
                                                  }}
                                                >
                                                  {localization(
                                                    "declined",
                                                  ).toUpperCase()}
                                                </div>
                                              )}
                                            </td>
                                            <td className="border-0 text-end align-middle">
                                              <div className="d-flex align-items-start gap-2 justify-content-end">
                                                <span>
                                                  {getCurrencySymbol(
                                                    currencyCode,
                                                  )}
                                                  {(item.price || 0).toFixed(2)}
                                                </span>
                                                {!repairOrderId && (
                                                  <button
                                                    type="button"
                                                    className={`btn btn-sm ${
                                                      isItemDeclined
                                                        ? "btn-outline-secondary"
                                                        : "btn-outline-danger"
                                                    }`}
                                                    onClick={() =>
                                                      handleDeclinePopup(
                                                        serviceKey,
                                                      )
                                                    }
                                                    disabled={
                                                      isApproved ||
                                                      isApproving ||
                                                      isItemDeclined ||
                                                      isDeclined
                                                    }
                                                  >
                                                    {isItemDeclined
                                                      ? localization("declined")
                                                      : localization("decline")}
                                                  </button>
                                                )}
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      },
                                    )}
                                    <tr
                                      style={{ borderTop: "2px solid #dee2e6" }}
                                    >
                                      <td></td>
                                      <td className="text-start text-muted">
                                        {localization("tax")} ({taxRate}%)
                                      </td>
                                      <td className="text-end text-muted">
                                        {getCurrencySymbol(currencyCode)}
                                        {taxAmount.toFixed(2)}
                                      </td>
                                    </tr>
                                    <tr
                                      style={{
                                        borderTop: "1px solid #dee2e6",
                                        fontSize: "1.05rem",
                                      }}
                                    >
                                      <td></td>
                                      <td className="text-start fw-bold">
                                        {localization("total")}
                                      </td>
                                      <td className="text-end fw-bold">
                                        {getCurrencySymbol(currencyCode)}
                                        {totalWithTax.toFixed(2)}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {!repairOrderId && (
                        <div className="card-footer bg-gray d-flex justify-content-center align-items-center gap-2">
                          <button
                            className="btn btn-secondary fw-bold"
                            onClick={() => {
                              handleApprovePopup();
                            }}
                            disabled={
                              isApproved ||
                              isApproving ||
                              activeRepairOrderItems.length === 0 ||
                              isDeclined
                            }
                          >
                            {localization("approve")}
                          </button>
                          <button
                            className="btn btn-red fw-bold"
                            disabled={isApproved || isApproving || isDeclined}
                            onClick={async () => {
                              handleDeclinePopup(null, true);
                            }}
                          >
                            {localization("decline")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* PDF Print Layout - Print Only */}
      <PrintableRepairOrder
        orderData={orderData}
        dealerLogoSrc={dealerLogoSrc}
        currentYear={currentYear}
        t={localization}
        currencyCode={currencyCode}
      />
    </div>
  );
};

export default PrintPDFPage;
